/**
 * #2919 — caches that belong to ONE signed-in account.
 *
 * Two caches can outlive a sign-out on a shared browser:
 *
 *   - the service worker's runtime cache `tarii-me-schedule` (offline
 *     "my schedule", vite.config.ts), keyed by URL only;
 *   - the react-query client, keyed by query key.
 *
 * Before #2919 the switcher read `/api/account/me/workspaces` through the
 * worker cache, so after a sign-in as another account it first listed the
 * previous account's teams. `clearAccountCaches()` drops both. It runs on
 * every sign-out (`AuthContext.tsx`) and when the signed-in account id
 * differs from the last one this browser saw (`noteAccountId`, called by
 * `<AccountCacheGuard/>`).
 */
import type { QueryClient } from "@tanstack/react-query";

export const ME_SCHEDULE_CACHE = "tarii-me-schedule";
export const LAST_ACCOUNT_KEY = "tarii.last_account_id";
const CACHE_DELETE_TIMEOUT_MS = 1_000;

let registeredClient: QueryClient | null = null;

/** `main.tsx` registers the app's one query client. */
export function setQueryClientForAccountCaches(client: QueryClient | null): void {
  registeredClient = client;
}

/** Delete the worker cache; resolves within about a second, never throws. */
function deleteWorkerCache(): Promise<void> {
  const storage = (globalThis as { caches?: CacheStorage }).caches;
  if (!storage) return Promise.resolve();
  let deletion: Promise<unknown>;
  try {
    deletion = storage.delete(ME_SCHEDULE_CACHE);
  } catch {
    return Promise.resolve();
  }
  const timeout = new Promise<void>((resolve) => setTimeout(resolve, CACHE_DELETE_TIMEOUT_MS));
  return Promise.race([deletion.then(() => undefined), timeout]).catch(() => undefined);
}

/**
 * Drop the worker's account cache and the react-query cache. Never
 * throws; resolves within about a second even when Cache Storage stalls,
 * so a sign-out never hangs on it. Both deletes start before the first
 * await.
 *
 * `repaint` (a change of account seen while the app stays open): instead
 * of `clear()`, which leaves mounted screens showing the old account's
 * data, drop the unused queries and, once the worker cache is gone,
 * reset the mounted ones so they refetch as the new account.
 */
export async function clearAccountCaches(opts: { repaint?: boolean } = {}): Promise<void> {
  const client = registeredClient;
  try {
    client?.getMutationCache().clear();
    if (opts.repaint) client?.removeQueries({ type: "inactive" });
    else client?.clear();
  } catch {
    // A failed clear must never block a sign-out.
  }
  await deleteWorkerCache();
  if (opts.repaint && client) {
    await client.resetQueries().catch(() => undefined);
  }
}

/**
 * #3005 — cancel the account's queries in flight, so a late answer for
 * the account leaving cannot refill the cache after the clear. Never
 * throws.
 */
export async function cancelAccountQueries(): Promise<void> {
  try {
    await registeredClient?.cancelQueries();
  } catch {
    // A failed cancel must never block a switch.
  }
}

function readLastAccountId(): string | null {
  try {
    return window.localStorage.getItem(LAST_ACCOUNT_KEY);
  } catch {
    return null;
  }
}

function writeLastAccountId(id: string | null): void {
  try {
    if (id === null) window.localStorage.removeItem(LAST_ACCOUNT_KEY);
    else window.localStorage.setItem(LAST_ACCOUNT_KEY, id);
  } catch {
    // Storage blocked: the sign-out clear still covers the common case.
  }
}

/**
 * Record the signed-in account. When this browser last saw a DIFFERENT
 * account, clear the account caches first. `null` forgets the stored id.
 */
export function noteAccountId(id: string | null): void {
  const last = readLastAccountId();
  if (id !== null && last !== null && last !== id) {
    void clearAccountCaches({ repaint: true });
  }
  writeLastAccountId(id);
}
